<template>
  <div class="v-form-question-upload">
    <VUpload
      v-if="showUpload"
      accept=".jpg,.png,.csv,.txt,.docx,.pdf,.eml,.xls,.xlsx,.zip"
      drag
      :error="props.blueprint.errors[question.id]?.answer"
      :max="10"
      :max-size="5e7"
      :multiple="question.allow_multiple_answers"
      :optional="question.optional"
      @update:files="onUpdateFiles"
    />
    <VFileList :files @remove-file="onRemoveFile">
      <template #file-name="{ file, index }">
        <slot :file :index name="file-name" :question="uploadQuestions[index]" />
      </template>
    </VFileList>
  </div>
</template>

<script setup lang="ts" generic="TBlueprint extends Blueprint = Blueprint">
import { computed } from 'vue'

import { type FileLike } from '../types.ts'
import { type Blueprint, type Question, type UploadQuestion } from './FormBuilder/useBlueprint.ts'
import VFileList from './Upload/VFileList.vue'
import VUpload from './Upload/VUpload.vue'

interface Props {
  blueprint: TBlueprint
  question: UploadQuestion
}

const props = defineProps<Props>()

defineSlots<{
  'file-name'(props: { file: FileLike; index: number; question: Question }): any
}>()

const uploadQuestions = computed((): UploadQuestion[] => {
  const question = props.question

  if (
    question.parent_question_id &&
    props.blueprint.questions.some((blueprintQuestion) => blueprintQuestion.id === question.parent_question_id && blueprintQuestion.type === 'upload')
  ) {
    return []
  }

  const questions = []

  for (const blueprintQuestion of props.blueprint.questions) {
    if (blueprintQuestion.type === 'upload' && blueprintQuestion.parent_question_id === question.id && blueprintQuestion.answer) {
      questions.push(blueprintQuestion)
    }
  }

  return questions
})

const files = computed((): FileLike[] => {
  const files = []
  for (const question of uploadQuestions.value) {
    if (!question.answer) {
      continue
    }

    files.push(typeof question.answer === 'string' ? { name: question.answer } : { name: question.answer.name, loading: true })
  }

  return files
})

const showUpload = computed(() => {
  if (props.question.allow_multiple_answers) {
    return true
  }

  return !files.value.length
})

async function onUpdateFiles(files: File[]) {
  delete props.blueprint.errors[props.question.id]
  for (const file of files) {
    await props.blueprint.addQuestion('upload', { answer: file, parent_question_id: props.question.id, internal: props.question.internal })
  }
}

async function onRemoveFile(index: number) {
  delete props.blueprint.errors[props.question.id]
  const question = uploadQuestions.value[index]
  await props.blueprint.removeQuestion(question)
}
</script>

<style>
.v-form-question-upload {
  width: 100%;
}
</style>
